import React from 'react';
import styled from '@emotion/styled';
import moment from 'moment-timezone';
import { Skeleton, Typography, theme } from '@robinpowered/ui-kit';
import { useTranslation } from 'react-i18next';
import {
  EmptyDeskIllustration,
  ReservationCard,
} from './components/DeskReservationCard';
import {
  ProfileReservation,
  ReservationMenuAction,
} from './utils/getReservationActions';

type BookingsListProps = {
  reservations: ProfileReservation[];
  isCurrentPerson: boolean;
  recurrenceByDeskId: Record<string, string | null>;
  currentTime: moment.Moment;
  onCardClick: (reservation: ProfileReservation) => void;
  onCheckIn: (reservation: ProfileReservation) => void;
  onMenuAction: (
    action: ReservationMenuAction,
    reservation: ProfileReservation
  ) => void;
  /** Lay desk-card actions out in a row (fullscreen Desks panel). */
  wideLayout?: boolean;
};

export const BookingsList = ({
  reservations,
  isCurrentPerson,
  recurrenceByDeskId,
  currentTime,
  onCardClick,
  onCheckIn,
  onMenuAction,
  wideLayout = false,
}: BookingsListProps): JSX.Element => {
  const { t } = useTranslation('peopleProfileSidebar', {
    keyPrefix: 'bookings',
  });
  const { token } = theme.useToken();

  if (reservations.length === 0) {
    return (
      <Section>
        <EmptyCard $borderColor={token.colorBorderSecondary}>
          <EmptyDeskIllustration width={140} height={60} />
          <Typography.Text type="secondary">{t('empty')}</Typography.Text>
        </EmptyCard>
      </Section>
    );
  }

  return (
    <Section>
      {reservations.map((reservation) => (
        <ReservationCard
          key={reservation.id}
          reservation={reservation}
          recurrence={recurrenceByDeskId[reservation.seat.id] ?? null}
          isCurrentPerson={isCurrentPerson}
          currentTime={currentTime}
          onCardClick={onCardClick}
          onCheckIn={onCheckIn}
          onMenuAction={onMenuAction}
          wideLayout={wideLayout}
        />
      ))}
    </Section>
  );
};

export const BookingsListSkeleton = (): JSX.Element => {
  const { token } = theme.useToken();
  return (
    <Section>
      <EmptyCard
        $borderColor={token.colorBorderSecondary}
        data-testid="bookings-card-skeleton"
      >
        <Skeleton active paragraph={{ rows: 2 }} title={false} />
      </EmptyCard>
    </Section>
  );
};

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin: 0 16px 16px;
`;

const EmptyCard = styled.div<{ $borderColor: string }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 24px 16px;
  border: 1px solid ${({ $borderColor }) => $borderColor};
  border-radius: 8px;
  text-align: center;
`;
